import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, OverlayTrigger, Tooltip, Accordion } from 'react-bootstrap';
import { formatToDecimals } from '@lib/helpers/formatting';
import ProjectDossierStream from 'components/projectDossier/ProjectDossierStream';
import ProjectDossierWp from 'components/projectDossier/ProjectDossierWp';
import DashboardWheelsSection from 'components/charts/DashboardWheelsSection';
import { PersonCircle, InfoCircleFill, IcoEmptyCalendar, IcoNewAnalysis, IcoFolder } from '@icons';
import { OETrans } from 'components/translation/OETrans';
import { translate } from 'components/translation/translation';
import { WithPermission } from '@lib/helpers/WithPermission';

const TriggerTooltip = ({ parentRef, textValue }) => {
	const [overflowActive, setOverflowActive] = useState(false);
	//const [style, setStyle] = useState(null);
	const childRef = useRef(null);
	const childRefInnerHTML = childRef.current?.innerHTML;

	useEffect(() => {
		if (parentRef.current && childRef.current && childRefInnerHTML) {
			//console.log('childRef offsetWidth', childRef.current.offsetWidth);
			//console.log('parentRef offsetWidth', parentRef.current.offsetWidth);

			setOverflowActive(parentRef.current.offsetWidth < childRef.current.offsetWidth);
			//setStyle({parent: parentRef.current.offsetWidth, child: childRef.current.offsetWidth})
		}
	}, [childRef, childRefInnerHTML, parentRef]);

	const ConditionalWrapper = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children);

	return (
		<ConditionalWrapper
			condition={overflowActive}
			wrapper={(children) => (
				<OverlayTrigger placement="top-start" overlay={<Tooltip className="">{textValue}</Tooltip>}>
					{children}
				</OverlayTrigger>
			)}
		>
			<span ref={childRef}>{textValue}</span>
		</ConditionalWrapper>
	);
};

const Summary = ({ project, summaryWheelsValues, setSummaryWheelsValues }) => {
	const [thisProject, setThisProject] = useState({});
	const [finalizedAmount, setFinalizedAmount] = useState(0);
	const ref1 = useRef(null);
	const ref2 = useRef(null);
	const ref3 = useRef(null);
	const ref4 = useRef(null);
	const ref5 = useRef(null);
	const ref6 = useRef(null);
	const ref7 = useRef(null);
	const ref8 = useRef(null);
	const ref9 = useRef(null);
	const ref10 = useRef(null);
	const ref11 = useRef(null);
	const ref12 = useRef(null);
	//console.log('thisProject', thisProject);

	const businessPlanContainerRef = useRef(null);

	const isWP = thisProject.streamConfigurationType === 'WP';

	useEffect(() => {
		setThisProject(project);
	}, [project]);

	//   useEffect(() => {
	//     if (summaryWheelsValues.length > 0) {
	//       let result = 0;
	//       const totalAmount = summaryWheelsValues
	//         .filter((el) => el.propertyName === 'totalAmount')
	//         .map((el) => el.propertyValue);
	//       const notReportableNotReportedAmount = summaryWheelsValues
	//         .filter((el) => el.propertyName === 'notReportableNotReportedAmount')
	//         .map((el) => el.propertyValue);
	//       //importo consuntivato = totale - in lavorazione
	//       result = totalAmount - notReportableNotReportedAmount;

	//       setFinalizedAmount(result);
	//     }
	//   }, [summaryWheelsValues]);

	useEffect(() => {
		if (summaryWheelsValues.length > 0) {
			setFinalizedAmount(summaryWheelsValues.filter((el) => el.propertyName === 'finalAmount')?.[0]?.realValue);
		}
	}, [summaryWheelsValues]);

	return (
		<>
			<WithPermission permission="view-dashboard-charts">
				<DashboardWheelsSection projectId={project.id} setSummaryWheelsValues={setSummaryWheelsValues} />
			</WithPermission>

			<div className="simple-box scenaritab">
				<div className="row d-flex align-items-center mb-4">
					<div className="col-4 ps-0">
						<h5 className="title-box">
							<OETrans code="common.registry" fallback={'Anagrafica'} />
						</h5>
					</div>
				</div>

				<div id="registry-summary">
					<Row xs={2} className="g-5">
						{/* TITOLARI */}
						<Col>
							<Row className="row-title-registry">
								<Col className="icon-registry col-auto ps-0 pe-2 d-flex justify-content-center align-items-center">
									<PersonCircle />
								</Col>
								<Col className="title-registry">{translate('common.registry.owners', 'Titolari')}</Col>
							</Row>

							<Row className="row-detail-registry">
								<Col className="label-detail-registry">
									{isWP ? translate('common.registry.coordinator', 'Coordinatore') : translate('common.registry.society', 'Società')}
								</Col>

								<Col className="value-detail-registry" ref={ref1}>
									<TriggerTooltip parentRef={ref1} textValue={thisProject.company?.name} />
								</Col>
							</Row>

							<Row className="row-detail-registry">
								<Col className="label-detail-registry">{translate('common.registry.projectResponsible', 'Responsabile di Progetto')}</Col>

								<Col className="value-detail-registry" ref={ref2}>
									<TriggerTooltip parentRef={ref2} textValue={thisProject.manager ?? <>N.D.</>} />
								</Col>
							</Row>

							<Row className="row-detail-registry">
								<Col className="label-detail-registry">{translate('common.registry.projectManager', 'Project Manager')}</Col>

								<Col className="value-detail-registry" ref={ref3}>
									<TriggerTooltip parentRef={ref3} textValue={thisProject.oeManager ?? <>N.D.</>} />
								</Col>
							</Row>

							{!isWP && (
								<Row className="row-detail-registry">
									<Col className="label-detail-registry">{translate('common.registry.localization', 'Localizzazione')}</Col>

									<Col className="value-detail-registry" ref={ref4}>
										<TriggerTooltip
											parentRef={ref4}
											textValue={thisProject.network ? thisProject.networkNote : (thisProject.location?.nomeComune ?? <>N.D.</>)}
										/>
									</Col>
								</Row>
							)}
						</Col>

						{/* DETTAGLI */}
						<Col>
							<Row className="row-title-registry">
								<Col className="icon-registry col-auto ps-0 pe-2">
									<InfoCircleFill />
								</Col>
								<Col className="title-registry">{translate('common.registry.details', 'Dettagli')}</Col>
							</Row>

							<Row className="row-detail-registry">
								<Col className="label-detail-registry">{translate('common.registry.fund', 'Fondo')}</Col>

								<Col className="value-detail-registry" ref={ref5}>
									<TriggerTooltip parentRef={ref5} textValue={thisProject?.notice?.fund?.name} />
								</Col>
							</Row>

							<Row className="row-detail-registry">
								<Col className="label-detail-registry">{translate('common.registry.notice', 'Bando')}</Col>

								<Col className="value-detail-registry" ref={ref6}>
									<TriggerTooltip parentRef={ref6} textValue={thisProject?.notice?.call} />
								</Col>
							</Row>

							<Row className="row-detail-registry">
								<Col className="label-detail-registry">{translate('common.registry.businessPlan', 'Incluso nel Business Plan')}</Col>

								<Col className="value-detail-registry" ref={businessPlanContainerRef}>
									<TriggerTooltip parentRef={businessPlanContainerRef} textValue={thisProject?.businessPlan ? 'Sì' : 'No'} />
								</Col>
							</Row>
						</Col>

						{/* TIMING */}
						<Col>
							<Row className="row-title-registry">
								<Col className="icon-registry col-auto ps-0 pe-2">
									<IcoEmptyCalendar />
								</Col>
								<Col className="title-registry">{translate('common.registry.timing', 'Timing')}</Col>
							</Row>

							<Row className="row-detail-registry">
								<Col className="label-detail-registry">{translate('common.registry.duration', 'Durata (mesi)')}</Col>

								<Col className="value-detail-registry" ref={ref10}>
									<TriggerTooltip parentRef={ref10} textValue={thisProject.durationInMonths} />
								</Col>
							</Row>

							<Row className="row-detail-registry">
								<Col className="label-detail-registry">{translate('common.registry.effectiveStart', 'Avvio effettivo')}</Col>

								<Col className="value-detail-registry" ref={ref11}>
									<TriggerTooltip
										parentRef={ref11}
										textValue={
											thisProject.startDate ? (
												new Date(thisProject.startDate).toLocaleDateString('en-GB')
											) : thisProject.forecastStartDate ? (
												new Date(thisProject.forecastStartDate).toLocaleDateString('en-GB')
											) : (
												<>N.D.</>
											)
										}
									/>
								</Col>
							</Row>

							<Row className="row-detail-registry">
								<Col className="label-detail-registry">{translate('common.registry.forecastEnd', 'Fine prevista')}</Col>

								<Col className="value-detail-registry" ref={ref12}>
									<TriggerTooltip
										parentRef={ref12}
										textValue={
											thisProject.endDate ? (
												new Date(thisProject.endDate).toLocaleDateString('en-GB')
											) : thisProject.forecastEndDate ? (
												new Date(thisProject.forecastEndDate).toLocaleDateString('en-GB')
											) : (
												<>N.D.</>
											)
										}
									/>
								</Col>
							</Row>
						</Col>

						{/* DIMENSIONE ECONOMICA */}
						<Col>
							<Row className="row-title-registry">
								<Col className="icon-registry col-auto ps-0 pe-2">
									<IcoNewAnalysis />
								</Col>
								<Col className="title-registry">{translate('common.registry.economicDimension', 'Dimensione economica')} (€)</Col>
							</Row>

							<Row className="row-detail-registry">
								<Col className="label-detail-registry">{translate('common.registry.projectCost', 'Costo progetto')}</Col>

								<Col className="value-detail-registry" ref={ref7}>
									<TriggerTooltip
										parentRef={ref7}
										textValue={formatToDecimals(
											summaryWheelsValues
												.filter((el) => el.propertyName === 'totalAmount')
												.map((el) => el.propertyValue ?? 0)
												.pop(),
										)}
									/>
								</Col>
							</Row>

							<Row className="row-detail-registry">
								<Col className="label-detail-registry">{translate('common.registry.grant', 'Contributo concesso')}</Col>

								<Col className="value-detail-registry" ref={ref8}>
									<TriggerTooltip
										parentRef={ref8}
										textValue={formatToDecimals(
											summaryWheelsValues
												.filter((el) => el.propertyName === 'totalApprovedAmount')
												.map((el) => el.propertyValue ?? 0)
												.pop(),
										)}
									/>
								</Col>
							</Row>

							<Row className="row-detail-registry">
								<Col className="label-detail-registry">{translate('common.registry.expense', 'Spesa effettuata')}</Col>

								<Col className="value-detail-registry" ref={ref9}>
									<TriggerTooltip parentRef={ref9} textValue={formatToDecimals(finalizedAmount)} />
								</Col>
							</Row>
						</Col>
					</Row>
				</div>

				{thisProject.description && (
					<Row>
						<div id="accordion-summary" className="mt-5 px-0">
							<Accordion flush>
								<Accordion.Item eventKey="0">
									<Accordion.Header>
										<IcoFolder />
										<span className="accordion-summary-title" style={{ fontSize: '18px' }}>
											{translate('common.registry.description', 'Descrizione')}
										</span>
									</Accordion.Header>
									<Accordion.Body className='f-500' style={{ color: '#1C2743'}}>{thisProject.description}</Accordion.Body>
								</Accordion.Item>
							</Accordion>
						</div>
					</Row>
				)}
			</div>

			<div className="simple-box scenaritab" style={{ marginTop: '2.5rem' }}>
				<div className="row d-flex align-items-center mb-4">
					<div className="col-4 ps-0">
						<h5 className="title-box">{translate('common.registry.projectDossier', 'Fascicolo di progetto')}</h5>
					</div>
				</div>

				<Row className="mt-3 px-3">
					{thisProject.streamConfigurationType === 'STREAM' ? (
						<ProjectDossierStream entityObj={thisProject} />
					) : (
						<ProjectDossierWp entityObj={thisProject} />
					)}
				</Row>
			</div>
		</>
	);
};
export default Summary;
